import { useCallback, useState, useEffect, useContext } from 'react';
import HistoryItem from './HistoryItem';
import { groupHistoryMetaItems } from '../../services/history';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { StorageService } from '../../services/storage';
import { useTranslation } from 'react-i18next';
import { NUM_DAYS_HISTORY } from '../../constants/common';
import { getHistoryMeta, changeConversationLabel } from '../../services/conversation';
import { STORAGE_KEYS } from '../../constants/storageKeys';
import { HistoryContext } from '../../context/HistoryContext';

const HISTORY_MENU_KEY = 'historyExpanded';

const History = () => {
	const navigate = useNavigate();
	const { id } = useParams();
	const { t } = useTranslation();
	const {
		refreshTrigger,
		refreshHistory,
		hasInitialFetch,
		markInitialFetch,
		getShouldFetch,
		historyMeta,
		setHistoryMeta,
	} = useContext(HistoryContext);
	const [groupedHistoryMeta, setGroupedHistoryMeta] = useState([]);
	const [isOpen, setIsOpen] = useState(() => {
		const storedValue = StorageService.getItem(HISTORY_MENU_KEY);
		return storedValue !== null ? storedValue : false;
	});
	const [filter, setFilter] = useState('');

	const toggleHistory = useCallback(() => {
		const newIsOpen = !isOpen;
		StorageService.setItem(HISTORY_MENU_KEY, newIsOpen, null);
		setIsOpen(newIsOpen);
	}, [isOpen]);

	useEffect(() => {
		const token = StorageService.getItem(STORAGE_KEYS.AUTH_TOKEN);
		if (!token) {
			return;
		}

		// Only fetch if:
		// 1. Initial fetch hasn't been done yet (first app load), OR
		// 2. refreshTrigger has changed (after delete or sendQuestion)
		const shouldFetch = getShouldFetch();

		if (!shouldFetch) {
			return;
		}

		const fetchHistoryMeta = async () => {
			const response = await getHistoryMeta(token, NUM_DAYS_HISTORY);
			if (response.hasOwnProperty('conversationMetas')) {
				setHistoryMeta(response.conversationMetas);
			}
		};

		fetchHistoryMeta();
		if (!hasInitialFetch) {
			markInitialFetch();
		}
	}, [refreshTrigger, hasInitialFetch, markInitialFetch, getShouldFetch]);

	useEffect(() => {
		if (!historyMeta.length) {
			return;
		}
		var groupedHistoryMeta = groupHistoryMetaItems(historyMeta, filter);
		setGroupedHistoryMeta(groupedHistoryMeta);
	}, [historyMeta, filter]);

	const onDeleteHistoryItem = useCallback(
		(itemId) => {
			setHistoryMeta((prev) => prev.filter((item) => item.conversationId !== itemId));

			if (id && Number(id) === itemId) {
				navigate('/');
			}

			refreshHistory();
		},
		[id, navigate, refreshHistory],
	);

	const onLabelChangeHistoryItem = useCallback(
		async (itemId, newLabel) => {
			// TODO: Update conversation meta with new label instead of refreshing the history
			const response = await changeConversationLabel(itemId, newLabel);
			if (response) {
				setHistoryMeta((prev) =>
					prev.map((item) => (item.conversationId === itemId ? { ...item, label: newLabel } : item)),
				);
			}
		},
		[setHistoryMeta],
	);

	return (
		<div className={`flex-col transition-all duration-300 ease-in-out  ${isOpen ? 'w-3xs' : 'w-14'}`}>
			{/* Expand or shrink history menu component */}
			<button onClick={toggleHistory} className='cursor-pointer py-3 px-4'>
				<i className={`${isOpen ? 'ri-menu-fold-line' : 'ri-menu-unfold-line'} text-primary-text text-2xl`}></i>
			</button>

			{/* Add filter input component here, when the history menu is open */}
			{isOpen && (
				<input
					type='text'
					placeholder={t('history.filter')}
					className='w-7/8 p-2 border border-gray-300 rounded-md ml-4 mb-2 mt-2'
					onChange={(e) => setFilter(e.target.value)}
				/>
			)}

			{/* Show history items */}
			<div className='px-4 pb-32 max-h-[calc(100dvh-117px)] overflow-y-auto'>
				{isOpen &&
					groupedHistoryMeta.map(([groupLabel, items]) => (
						<div key={groupLabel}>
							<p className='pt-4 pb-2 px-1 text-xs font-medium focus-visible:outline-none '>
								{isNaN(groupLabel) ? t(`history.labels.${groupLabel}`) : groupLabel}
							</p>
							{items.map((item) => (
								<HistoryItem
									id={item.conversationId}
									key={item.conversationId}
									title={item.label ? item.label : item.question}
									isActive={id ? Number(id) === item.conversationId : false}
									onClick={() => navigate(`/query/${item.conversationId}`)}
									onDelete={() => onDeleteHistoryItem(item.conversationId)}
									onLabelChange={(newLabel) =>
										onLabelChangeHistoryItem(item.conversationId, newLabel)
									}
								/>
							))}
						</div>
					))}
			</div>
		</div>
	);
};

export default History;
